import { PERMISSIONS_API } from '@Constants/backendConstants';
import { useSession } from '@Contexts/session';
import { type ApiGroup, type ApiPermission } from '@Model';
import { useLinkedListApi } from '@Hooks/useListApi';
import { usePermissionsRepository } from '@Hooks/useRepository';
import {
    ApplicationsGroupRepository,
    ApplicationsRepository,
    UsersGroupRepository,
    UsersRepository,
} from '@Repositories/Permissions';
import { unwrapOr } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

const emptyApiPermissions: ApiPermission[] = [];
const emptyApiGroups: ApiGroup[] = [];

export function usePermissionsWithGroups(resource: 'users' | 'applications', id: string) {
    const {
        current: { partnershipId },
    } = useSession();
    const users = usePermissionsRepository(UsersRepository);
    const applications = usePermissionsRepository(ApplicationsRepository);
    const userGroups = useLinkedListApi(UsersGroupRepository, PERMISSIONS_API, id);
    const applicationGroups = useLinkedListApi(ApplicationsGroupRepository, PERMISSIONS_API, id);
    const loadLinkedGroups = resource === 'users' ? userGroups.loadPage : applicationGroups.loadPage;

    const { data: permissionsData, refetch: refetchPermissions } = useQuery({
        queryFn: () => (resource === 'users' ? users.getUserPermissions(id) : applications.getUserPermissions(id)),
        queryKey: [resource, id, 'permissions', partnershipId],
    });

    const { data: groupsData, refetch: refetchGroups } = useQuery({
        queryFn: () => loadLinkedGroups({ page_no: 0, page_size: 200, full_text_search: '', sort_columns: [] }),
        queryKey: [resource, id, 'groups', partnershipId],
    });

    const permissions: ApiPermission[] = unwrapOr(permissionsData, emptyApiPermissions);
    const groups: ApiGroup[] = groupsData?.items ?? emptyApiGroups;

    const refetch = useCallback(() => {
        void refetchPermissions();
        void refetchGroups();
    }, [refetchPermissions, refetchGroups]);

    return { permissions, refetch, groups };
}
